// setting_bank deposit/withdraw usage gating — CLIENT mirror.
//
// This is the front-end twin of api/routes/method/bank-usage.js and MUST stay
// in lock-step with it. Each company bank (setting_bank) carries two flags,
// use_deposit and use_withdraw. The rule:
//   - both flags 0 (unchecked) -> usable in BOTH deposit and withdraw sections
//     (backward-compatible default: existing banks are 0/0, so nothing changes
//     until an operator ticks a box).
//   - exactly one flag set      -> usable only in that section.
//   - both flags set            -> usable in both (explicit).
//
// Combined topup/withdraw screens (manual / auto / seamless) fetch every bank in
// one call (for the Bank Summary) and then split the deposit vs withdraw bank
// selectors client-side with these helpers, so a bank flagged deposit-only never
// appears in the withdraw payout dropdown and vice-versa.

export type BankUsage = 'deposit' | 'withdraw';

function normalizeUsage(usage: any): BankUsage | null {
  if (typeof usage !== 'string') return null;
  const u = usage.trim().toLowerCase();
  return u === 'deposit' || u === 'withdraw' ? (u as BankUsage) : null;
}

// Is `bank` usable in the given usage context? Unknown context -> no filtering.
export function bankAllowsUsage(bank: any, usage: any): boolean {
  const u = normalizeUsage(usage);
  if (!u) return true;

  const b = bank || {};
  const useDeposit = Number(b.use_deposit) === 1;
  const useWithdraw = Number(b.use_withdraw) === 1;

  if (!useDeposit && !useWithdraw) return true; // both unchecked -> both
  return u === 'deposit' ? useDeposit : useWithdraw;
}

// Filter a bank list down to those usable in the given section.
export function filterBanksByUsage(banks: any, usage: BankUsage): any[] {
  if (!Array.isArray(banks)) return [];
  return banks.filter((b) => bankAllowsUsage(b, usage));
}

// Is this bank flagged "Show at App" (public / show_at_app = 1)? The API aliases
// setting_bank.public as show_at_app; accept either key. Anything not explicitly
// 1 (0, null, undefined, "") is treated as NOT public.
export function bankIsPublic(bank: any): boolean {
  const b = bank || {};
  const v = b.show_at_app !== undefined && b.show_at_app !== null ? b.show_at_app : b.public;
  return Number(v) === 1;
}

// Panel topup/withdraw DROPDOWN filter. Applies the usage split, then OPTIONALLY
// drops banks marked "Do Not Show" (public/show_at_app = 0).
//
// The public-hiding step is OPT-IN per company (setting
// `panel_hide_nonpublic_banks`, default OFF). Rationale: some tenants curate the
// "Show at App" flag to keep receiving / NO-ATM banks out of the operator dropdown
// (they set only the banks they want visible to public=1), while other tenants
// never touched the flag (every bank is public=0) and expect all banks to show.
// A single global filter can't serve both, so the hide is gated per company and
// defaults OFF — tenants who never opt in are completely unaffected.
//
// This changes ONLY the dropdown. Callers keep the full `banks` list for the Bank
// Summary/balance table, and no DB flag is ever mutated. The player-facing app
// reads `public` on its own separate path — untouched here.
export function filterBanksForDropdown(banks: any, usage: BankUsage, hideNonPublic: boolean = false): any[] {
  const byUsage = filterBanksByUsage(banks, usage);
  if (!hideNonPublic) return byUsage;
  return byUsage.filter((b) => bankIsPublic(b));
}

// Normalize a company-setting flag that may arrive as a real boolean (from the
// /company/setting endpoint) or as a "true"/"false" string. Guards against the
// JS gotcha that `!!"false"` is truthy — only these values count as ON.
export function settingIsOn(v: any): boolean {
  return v === true || v === 1 || v === '1' || v === 'true';
}
